import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import CompanyPlace, { CompanyPlaceFilter } from "@/models/CompanyPlace";
import CompanyService from "@/service/CompanyService";

@Module({
  namespaced: true,
  name: "currentCompany",
})
export default class CurrentCompany extends VuexModule {
  _companyPlaces: CompanyPlace[] = [];
  _searchCompanyPlaces: CompanyPlace[] = [];

  get companyPlaces() {
    return this._companyPlaces || [];
  }

  get searchCompanyPlaces() {
    return this._searchCompanyPlaces || [];
  }

  @Mutation
  setCompanyPlaces(payload: CompanyPlace[]) {
    this._companyPlaces = payload;
  }

  @Mutation
  addCompanyPlaces(payload: CompanyPlace[]) {
    this._companyPlaces.push(...payload);
  }

  @Mutation
  $addSearchCompanyPlaces(payload: CompanyPlace[]) {
    this._searchCompanyPlaces.push(...payload);
  }

  @Mutation
  removeCompanyPlace(id: string) {
    const index = this._companyPlaces.findIndex((o) => o.id === id);
    if (index != undefined) {
      this._companyPlaces.splice(index, 1);
    }
  }

  @Mutation
  addCompanyPlace(payload: CompanyPlace) {
    this._companyPlaces.push(payload);
  }

  @Mutation
  updateCompanyPlace(payload: CompanyPlace) {
    const value = this._companyPlaces.find((o) => o.id === payload.id);
    Object.assign({}, value, payload);
  }

  @Mutation
  $updateSearchCompanyPlace(payload: CompanyPlace) {
    const value = this._searchCompanyPlaces.find(
      (o) => o.sourceId === payload.sourceId,
    );
    Object.assign({}, value, payload);
  }

  @Mutation
  $updateSelectionCompanyPlace(payload: CompanyPlace) {
    let value = this._searchCompanyPlaces.find((o) => o.id === payload.id);
    if (value) value.highlighted = payload.highlighted;
  }

  @Mutation
  $openModalPoiDetails(payload: CompanyPlace) {}

  @Mutation
  $clearMarkers() {}

  @Mutation
  $saveScouting() {}

  @Mutation
  $centerOnMarker(CompanyPlace: CompanyPlace) {}

  @Mutation
  $centerOnMarkers() {}

  @Mutation
  $setSearchCompanyPlaces(payload: CompanyPlace[]) {
    this._searchCompanyPlaces = payload;
  }

  @Mutation
  $addOrReplace(payload: CompanyPlace) {
    const candindate = this._companyPlaces.find((c) => c.id === payload.id);
    payload.highlighted = true;
    if (candindate) {
      this._companyPlaces = this._companyPlaces.reduce(
        (acc: CompanyPlace[], place: CompanyPlace) => {
          if (place.id === payload.id) {
            acc.push(payload);
          } else {
            acc.push(place);
          }
          return acc;
        },
        [],
      );
    } else {
      this._companyPlaces.push(payload);
    }
  }

  @Action({ rawError: true })
  async saveCompanyPlace(companyPlace: CompanyPlace) {
    const { commit } = this.context;
    const payload = companyPlace.toJson;
    if (this._companyPlaces.find((o) => o.id === companyPlace.id)) {
      const resp = await CompanyService.updateCompanyPlace(companyPlace);
      commit("updateCompanyPlace", resp);
    } else {
      const resp = await CompanyService.createCompanyPlace(companyPlace);
      commit("addCompanyPlace", resp);
    }
  }

  @Action({ rawError: true })
  async saveCompanyPlaceOffline(place: CompanyPlace) {
    const { commit } = this.context;
    commit("updateCompanyPlace", place);
  }

  @Action({ rawError: true })
  async destroyCompanyPlace(id: string) {
    const { commit } = this.context;
    const resp = await CompanyService.destroyCompanyPlace(id);
    commit("removeCompanyPlace", id);
  }

  @Action({ rawError: true })
  async saveCompanyPlaces(places: CompanyPlace[]) {
    const { commit } = this.context;
    for (let i = 0; i < places.length; i++) {
      if (this._companyPlaces.find((o) => o.sourceId === places[i].sourceId)) {
        continue;
      }
      await CompanyService.createCompanyPlace(places[i]);
    }
    commit("addCompanyPlaces", places);
    commit("$setSearchCompanyPlaces", []);
  }

  @Action({ rawError: true })
  addSearchCompanyPlaces(payload: CompanyPlace[]) {
    const { commit } = this.context;
    commit("$addSearchCompanyPlaces", payload);
  }

  @Action({ rawError: true })
  openModalPoiDetails(payload: CompanyPlace) {
    const { commit } = this.context;
    commit("$openModalPoiDetails", payload);
  }

  @Action({ rawError: true })
  updateSearchCompanyPlace(payload: CompanyPlace) {
    const { commit } = this.context;
    commit("$updateSearchCompanyPlace", payload);
  }

  @Action({ rawError: true })
  updateSelectionCompanyPlace(payload: CompanyPlace) {
    const { commit } = this.context;
    commit("$updateSelectionCompanyPlace", payload);
  }

  @Action({ rawError: true })
  setSearchCompanyPlaces(payload: CompanyPlace[]) {
    const { commit } = this.context;
    commit("$setSearchCompanyPlaces", payload);
  }

  @Action({ rawError: true })
  async clearMarkers() {
    const { commit } = this.context;
    commit("$clearMarkers");
  }

  @Action({ rawError: true })
  centerOnMarkers() {
    const { commit } = this.context;
    commit("$centerOnMarkers");
  }

  @Action({ rawError: true })
  async centerOnMarker(CompanyPlace: CompanyPlace) {
    const { commit } = this.context;
    commit("$centerOnMarker", CompanyPlace);
  }

  @Action({ rawError: true })
  async loadCompanyPlaces(companyId: string) {
    const { commit } = this.context;
    try {
      const filter = new CompanyPlaceFilter();
      filter.companyId = companyId;
      const resp = await CompanyService.getCompanyPlaces(filter);
      resp.forEach((o: CompanyPlace) => (o.highlighted = true));
      const sortedResp = resp.sort((a: CompanyPlace, b: CompanyPlace) => {
        if (a.incomplete && !b.incomplete) {
          return -1;
        }
        if (b.incomplete && !a.incomplete) {
          return 1;
        }
        return 0;
      });
      commit("setCompanyPlaces", sortedResp);
      return resp;
    } catch (e) {
      commit("setCompanyPlaces", []);
    }
  }

  @Action({ rawError: true })
  async addOrReplace(companyPlace: CompanyPlace) {
    const { commit } = this.context;
    commit("$addOrReplace", companyPlace);
  }
}
